import {CM_LOGGER_SCENE} from "@app/common/services/logging"
import {Component, inject, OnInit, computed, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"
import {
    SelectionPossibilities,
    ResolvedSelectionPosibility,
    ValueSlotComponent,
    resolveSelectionPossibilityValue,
} from "@app/template-editor/components/value-slot/value-slot.component"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {
    MeshCurve,
    PositionControlPoint,
    UVControlPoint,
    SceneNodes,
    getControlPointPartNumber,
    isControlPointPart,
    ObjectPathPart,
    getRootTemplateNodeForObjectPath,
} from "@cm/template-nodes"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {ListItemComponent} from "@common/components/item/list-item/list-item.component"
import {BaseCurveInspectorComponent} from "@template-editor/components/inspectors/base-curve-inspector/base-curve-inspector.component"
import {objectPathPartEquals, SceneNodePartClickEvent} from "@app/template-editor/services/scene-manager.service"
import {Vector3, Vector2} from "@cm/math"
import {z} from "zod/v4-mini"

type CoordinateSpace = {space: "position"} | {space: "uv"; channel: 0 | 1}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-mesh-curve-inspector",
    templateUrl: "./mesh-curve-inspector.component.html",
    styleUrl: "./mesh-curve-inspector.component.scss",
    imports: [
        InspectorSectionComponent,
        ValueSlotComponent,
        ButtonComponent,
        InputContainerComponent,
        ListItemComponent,
        MatMenuModule,
        MatTooltipModule,
        StringInputComponent,
    ],
})
export class MeshCurveInspectorComponent extends BaseCurveInspectorComponent<MeshCurve> implements OnInit {
    private readonly logger = inject(CM_LOGGER_SCENE)

    readonly $booleanSchema = signal(z.boolean())

    readonly $mesh = computed(() => this.$parameters().mesh)
    readonly $coordinateSpacePossibilities = computed(() => {
        const mesh = this.$mesh()
        if (!mesh) return []

        const sceneMesh = this.getSceneMesh()
        if (!sceneMesh) return []
        const uvs = sceneMesh.completeMeshData.reified.uvs

        const possibilities: SelectionPossibilities<CoordinateSpace> = [{name: "Position", value: {space: "position"}}]
        if (uvs.length > 0) possibilities.push({name: "UV 0", value: {space: "uv", channel: 0}})
        if (uvs.length > 1) possibilities.push({name: "UV 1", value: {space: "uv", channel: 1}})
        if (uvs.length > 2) this.logger.warn("More than 2 UV channels are not supported")
        return possibilities
    })
    readonly $canSetCoordinateSpace = computed(() => this.$mesh() !== null && this.$controlPoints().length == 0)
    readonly $coordinateSpace = computed(() => {
        const uvChannel = this.$parameters().uvChannel
        return this.resolveCoordinateSpaceValueToSelectionPossibility(uvChannel !== undefined ? {space: "uv", channel: uvChannel} : {space: "position"})
    })

    override ngOnInit() {
        super.ngOnInit()

        this.sceneManagerService.templateTreeChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((differences) => {
            const node = this.$node()

            const changes = differences.modifiedNodes.get(node)
            if (changes) {
                const selectedNodePartsToRemove = this.sceneManagerService.$selectedRootNodeParts().filter(({templateNode, part}) => {
                    if (templateNode !== node || !isControlPointPart(part)) return false

                    const index = getControlPointPartNumber(part)
                    const change = changes.find((change) => change.path === `controlPoints.${index}`)
                    if (!change) return false

                    return change.newValue === undefined
                })

                if (selectedNodePartsToRemove.length > 0) {
                    const partsToRemove: ObjectPathPart[] = selectedNodePartsToRemove.map((snp) => ({path: [snp.templateNode], part: snp.part}))
                    const filtered = this.sceneManagerService.$selectedObjectPathParts().filter((p) => !partsToRemove.some((r) => objectPathPartEquals(p, r)))
                    const stillHasNodeAsRoot = filtered.some((p) => getRootTemplateNodeForObjectPath(p.path) === node)
                    this.sceneManagerService.selectNodes(stillHasNodeAsRoot ? filtered : [...filtered, {path: [node], part: "root"}])
                }
            }
        })
    }

    override addPointImpl(node: MeshCurve) {
        const canNotAdd = {canAdd: false as const}

        if (!node) return canNotAdd

        const transform = this.sceneManagerService.getTransformAccessor(node)?.getTransform()

        if (!transform) return canNotAdd

        const meshCurveControl = this.getMeshCurveControlForNode(node)
        if (!meshCurveControl) return canNotAdd

        return {
            canAdd: true as const,
            getNewControlPointsWithInsertionPosition: (sceneNodePartClickEvent: SceneNodePartClickEvent) => {
                const meshIntersection = sceneNodePartClickEvent.target.find(({sceneNodePart}) => sceneNodePart.sceneNode.id === meshCurveControl.mesh.id)
                if (!meshIntersection) return

                const {intersection} = meshIntersection
                if (!intersection) return

                const {point, normal, uv, uv1} = intersection
                if (!normal) return

                const coodinateSpace = this.$coordinateSpace()
                if (!coodinateSpace) return

                const inverseTransform = transform.inverse()
                const surfacePoint = Vector3.fromArray(inverseTransform.multiplyVectorXYZW(point.x, point.y, point.z, 1.0))
                const surfaceNormal = new Vector3(normal.x, normal.y, normal.z).normalized()

                const usingUVSpace = resolveSelectionPossibilityValue(coodinateSpace.value).space === "uv"
                const uvChannel = node.parameters.uvChannel
                if (usingUVSpace && uvChannel === undefined) return
                if (usingUVSpace && ((uvChannel === 0 && uv === undefined) || (uvChannel === 1 && uv1 === undefined))) return

                const curveIntersection = sceneNodePartClickEvent.target.find(({sceneNodePart}) => sceneNodePart.sceneNode === meshCurveControl)
                const insertionPosition = this.getInsertionPositionForAddedControlPoint(curveIntersection, surfacePoint)

                const controlPoints = (() => {
                    if (usingUVSpace) {
                        const surfaceUV = uvChannel === 0 ? new Vector2(uv!.x, uv!.y) : new Vector2(uv1!.x, uv1!.y)
                        const points = [...node.parameters.controlPoints] as UVControlPoint[]
                        if (insertionPosition !== undefined) points.splice(insertionPosition, 0, {uv: surfaceUV.toArray(), corner: false, sectionBreak: false})
                        else points.push({uv: surfaceUV.toArray(), corner: false, sectionBreak: false})
                        return points
                    } else {
                        const points = [...node.parameters.controlPoints] as PositionControlPoint[]
                        if (insertionPosition !== undefined)
                            points.splice(insertionPosition, 0, {
                                position: surfacePoint.toArray(),
                                normal: surfaceNormal.toArray(),
                                corner: false,
                                sectionBreak: false,
                            })
                        else points.push({position: surfacePoint.toArray(), normal: surfaceNormal.toArray(), corner: false, sectionBreak: false})
                        return points
                    }
                })()

                return {controlPoints, insertionPosition}
            },
            prefixPath: undefined,
        }
    }

    setCoordinateSpace(space: CoordinateSpace) {
        if (!this.$canSetCoordinateSpace()) return
        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({uvChannel: space.space === "uv" ? space.channel : undefined})
        })
    }

    resolveCoordinateSpaceValueToSelectionPossibility(value: CoordinateSpace): ResolvedSelectionPosibility<CoordinateSpace> | undefined {
        const possibilities = this.$coordinateSpacePossibilities().map((possibility) => ({
            ...possibility,
            value: resolveSelectionPossibilityValue(possibility.value),
        }))
        return possibilities.find((possibility) => {
            if (value.space === "position") return possibility.value.space === "position"
            return possibility.value.space === "uv" && possibility.value.channel === value.channel
        })
    }

    getSceneMesh() {
        const mesh = this.$mesh()
        if (!mesh) return

        const sceneMeshParts = this.sceneManagerService.getSceneNodes(mesh).filter(SceneNodes.Mesh.is)
        if (sceneMeshParts.length !== 1) {
            return
        }
        return sceneMeshParts[0]
    }

    protected readonly resolveSelectionPossibilityValue = resolveSelectionPossibilityValue
}
